import React, { useEffect, useState } from 'react';
import { Button, MessageBarBody } from '@fluentui/react-components';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

// models
import { ProtocolVisit } from '../../../../../models/Entities/Protocols/ProtocolVisit';

// component functions
import * as ComponentFunctions from './StageItemLink.Functions';

// components
import ProtocolVisitSelectorComponent from '../../../../../commons/component/Inputs/ProtocolVisitsSelector/ProtocolVisitSelector.Component';
import { GuideDrawer, GuideDrawerSection } from '../../../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../../../theme/components/GuideMessageBar';

// styles
import './StageItemLink.Styles.css';
import { Delete20Regular, Group20Regular, Link20Regular } from '@fluentui/react-icons';
import StageItemBadgeComponent from '../StageItemBadge/StageItemBadge.Component';

export const StageItemLinkComponent = (props: ComponentFunctions.Props) => {
    const [t] = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedVisits, setSelectedVisits] = useState<ProtocolVisit[]>([]);
    const [formError, setFormError] = useState<string | undefined>(undefined);
    const protocolId = props.ProtocolId;
    const associatedVisitIds = props.StageItems
        .map((item) => item.ermProtocolVisitId)
        .filter((id): id is number => id !== undefined && id !== null);

    useEffect(() => {
        if (isOpen && props.ContractStage?.id) {
            props.GetAllContractStageItems(props.ContractStage.id);
        }
    }, [isOpen, props.ContractStage?.id]);

    const openPanel = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setFormError(undefined);
        setSelectedVisits([]);
        setIsOpen(true);
    };

    const dismissPanel = () => {
        setFormError(undefined);
        setSelectedVisits([]);
        setIsOpen(false);
    };

    const onVisitSelected = (visit: ProtocolVisit[] | ProtocolVisit | undefined) => {
        if (Array.isArray(visit)) {
            setSelectedVisits(visit);
            return;
        }

        setSelectedVisits(visit ? [visit] : []);
    };

    const handleAddAssociation = () => {
        if (!selectedVisits || selectedVisits.length === 0) {
            setFormError(t('stageItemLink.validation.requiredVisit'));
            return;
        }

        setFormError(undefined);

        selectedVisits.forEach((visit: ProtocolVisit) => {
            const alreadyAssociated = props.StageItems.some(
                (item) => item.ermProtocolVisitId === visit.crossVersionId
            );

            if (alreadyAssociated) {
                return;
            }

            props.AddContractStageItem(props.ContractStage.id, {
                contractStageId: props.ContractStage.id,
                ermProtocolVisitId: visit.crossVersionId,
                emrProtocolVisitName: visit.title
            });
        });

        setSelectedVisits([]);
    };

    const handleDeleteAssociation = (
        event: any,
        stageItemId: number | undefined
    ) => {
        event.stopPropagation();
        if (!stageItemId) return;

        props.DeleteContractStageItem(props.ContractStage.id, stageItemId);
    };

    return (
        <>
            <div className="sil-trigger">
                <Button
                    icon={<Group20Regular />}
                    appearance="subtle"
                    size="small"
                    title={t('stageItemLink.button.title')}
                    aria-label={t('stageItemLink.button.title')}
                    onClick={openPanel}
                />
                <StageItemBadgeComponent ContractStage={props.ContractStage} />
            </div>

            <GuideDrawer
                open={isOpen}
                size="small"
                eyebrow={props.ContractStage?.stageName || undefined}
                title={t('stageItemLink.panel.header')}
                onDismiss={dismissPanel}
                closeLabel={t('stageItemLink.footer.closeButton')}
                testId="stage-item-link-drawer"
                secondaryAction={{ label: t('stageItemLink.footer.closeButton') }}
                primaryAction={{
                    label: t('stageItemLink.footer.associateButton'),
                    onClick: handleAddAssociation,
                    disabled: !props.ProtocolId,
                    icon: <Link20Regular />
                }}
            >
                {!props.ProtocolId && (
                    <GuideMessageBar intent="warning">
                        <MessageBarBody>{t('stageItemLink.messages.noProtocol')}</MessageBarBody>
                    </GuideMessageBar>
                )}

                {formError && (
                    <GuideMessageBar intent="error">
                        <MessageBarBody>{formError}</MessageBarBody>
                    </GuideMessageBar>
                )}

                {/* ── Selector section ── */}
                {protocolId !== undefined && (
                    <GuideDrawerSection title={t('stageItemLink.props.selectVisit', 'Seleccionar visita del protocolo')}>
                        <ProtocolVisitSelectorComponent
                            ProtocolId={protocolId}
                            onSelectedVisit={onVisitSelected}
                            SelectedVisitIds={selectedVisits.map((visit) => visit.crossVersionId)}
                            ExcludedVisitIds={associatedVisitIds}
                            MultiselectEnable={true}
                        />
                    </GuideDrawerSection>
                )}

                {/* ── Associations list ── */}
                <GuideDrawerSection title={t('stageItemLink.props.associatedVisits')}>
                    {props.StageItems.length === 0 ? (
                        <div className="sil-empty">
                            <span>{t('stageItemLink.props.emptyAssociations')}</span>
                        </div>
                    ) : (
                        <div className="sil-list">
                            {props.StageItems.map((item, index) => (
                                <div className="sil-item" key={`${item.id ?? 'item'}-${index}`}>
                                    <span className="sil-item-index">{index + 1}</span>
                                    <span className="sil-item-name">{item.emrProtocolVisitName}</span>
                                    <Button
                                        appearance="subtle"
                                        size="small"
                                        icon={<Delete20Regular />}
                                        aria-label={t('stageItemLink.props.removeButton')}
                                        title={t('stageItemLink.props.removeButton')}
                                        onClick={(event) => handleDeleteAssociation(event, item.id)}
                                        disabled={!item.id}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </GuideDrawerSection>
            </GuideDrawer>
        </>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(StageItemLinkComponent as any);
